import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FolderKanban, Store as StoreIcon, Users as UsersIcon, IndianRupee, TrendingUp, Plus } from "lucide-react";
import { formatINR, formatNum, formatLakhs } from "@/lib/format";
import { useNavigate } from "react-router-dom";

/**
 * Super-Admin portfolio dashboard — cross-project rollup.
 * Aggregates revenue / cups / stores / users across all projects and drills into any one.
 */
export default function SuperAdminPortfolio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio/summary");
      setData(data);
    } catch { toast.error("Failed to load portfolio"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Portfolio" subtitle="Super-Admin · Cross-project view" />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
        {/* Portfolio KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 rounded-xl border-gray-200 bg-white" data-testid="portfolio-kpi-projects">
            <div className="flex items-center gap-2"><FolderKanban className="h-4 w-4 text-brand-red" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Projects</div></div>
            <div className="font-display text-2xl font-black mt-1">{data?.totals.projects || 0}</div>
          </Card>
          <Card className="p-4 rounded-xl border-gray-200 bg-white" data-testid="portfolio-kpi-stores">
            <div className="flex items-center gap-2"><StoreIcon className="h-4 w-4 text-brand-red" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Stores</div></div>
            <div className="font-display text-2xl font-black mt-1">{formatNum(data?.totals.stores || 0)}</div>
            <div className="text-[10px] text-gray-400">{data?.totals.active_stores || 0} active today</div>
          </Card>
          <Card className="p-4 rounded-xl border-gray-200 bg-white" data-testid="portfolio-kpi-users">
            <div className="flex items-center gap-2"><UsersIcon className="h-4 w-4 text-brand-red" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Users</div></div>
            <div className="font-display text-2xl font-black mt-1">{formatNum(data?.totals.users || 0)}</div>
          </Card>
          <Card className="p-4 rounded-xl border-brand-red bg-red-50/50" data-testid="portfolio-kpi-revenue">
            <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-brand-red" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-brand-red">Revenue this month</div></div>
            <div className="font-display text-2xl font-black mt-1 text-brand-red">{formatLakhs(data?.totals.revenue_month || 0)}</div>
            <div className="text-[10px] text-gray-500">Today: {formatINR(data?.totals.revenue_today || 0)}</div>
          </Card>
        </div>

        {/* Projects table */}
        <Card className="p-5 rounded-xl border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Projects</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Portfolio breakdown</div>
            </div>
            <Button onClick={() => navigate("/admin/projects")} className="bg-brand-red hover:bg-[#B31721] text-white" data-testid="manage-projects-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Manage projects
            </Button>
          </div>
          {loading ? <div className="p-8 text-center text-gray-500">Loading…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="portfolio-project-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Project</th>
                    <th className="text-left px-3 py-2">Coca-Cola</th>
                    <th className="text-right px-3 py-2">Stores</th>
                    <th className="text-right px-3 py-2">Users</th>
                    <th className="text-right px-3 py-2">Cups today</th>
                    <th className="text-right px-3 py-2">Revenue today</th>
                    <th className="text-right px-3 py-2">Revenue this month</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.projects || []).map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate("/admin/projects")}
                      data-testid={`portfolio-row-${p.code || p.id.slice(0, 8)}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold flex items-center gap-1.5">
                          {p.name}
                          {p.is_default && <Badge className="bg-gray-100 text-gray-600 border border-gray-200 h-4 text-[9px] px-1.5">Default</Badge>}
                        </div>
                        <div className="text-[10px] text-gray-500">{p.code}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{p.client_name || "—"}</td>
                      <td className="px-3 py-2 text-right font-semibold">{p.stores}</td>
                      <td className="px-3 py-2 text-right">{p.users}</td>
                      <td className="px-3 py-2 text-right">{formatNum(p.cups_today)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatINR(p.revenue_today)}</td>
                      <td className="px-3 py-2 text-right font-bold text-brand-red">{formatINR(p.revenue_month)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
